'use client'

import { Carousel, CarouselItem } from "@/components/elements/Carousel"
import { formatImageURL, formatImageURLGoogle, getActiveBanners } from "@/utils/util"
import { navigateUrl } from "@/utils/client-request"
import { usePathname } from "next/navigation"
import { useSession } from "next-auth/react"
import Image from "next/image"

const shimmer = (w: number, h: number) => `
<svg width="${w}" height="${h}" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
  <defs>
    <linearGradient id="g">
      <stop stop-color="#f6f7f8" offset="20%" />
      <stop stop-color="#edeef1" offset="50%" />
      <stop stop-color="#f6f7f8" offset="70%" />
    </linearGradient>
  </defs>
  <rect width="${w}" height="${h}" fill="#f6f7f8" />
  <rect id="r" width="${w}" height="${h}" fill="url(#g)" />
  <animate xlink:href="#r" attributeName="x" from="-${w}" to="${w}" dur="1s" repeatCount="indefinite"  />
</svg>`

const toBase64 = (str: string) =>
  typeof window === 'undefined'
    ? Buffer.from(str).toString('base64')
    : window.btoa(str)

const RATIO_CLASS: Record<string, string> = {
  '9/4': 'aspect-[9/4]',
  '16/9': 'aspect-[16/9]',
  '4/3': 'aspect-[4/3]',
  '21/9': 'aspect-[21/9]',
  '3/2': 'aspect-[3/2]',
}

const RATIO_DIMS: Record<string, { w: number, h: number }> = {
  '9/4': { w: 1800, h: 800 },
  '16/9': { w: 1600, h: 900 },
  '4/3': { w: 1600, h: 1200 },
  '21/9': { w: 2100, h: 900 },
  '3/2': { w: 1800, h: 1200 },
}

type Campaign = {
  id: string
  name: string
  handle: string
  campaignBanner?: string
  customerOnly?: boolean
}

type Slide = { src: string, alt: string, link?: string }

export default function Hero({ vendor, activeCampaigns = [] }: { vendor: any, activeCampaigns?: Campaign[] }) {
  const pathname = usePathname()
  const { data: session } = useSession()

  const liveVendorBanners = getActiveBanners(vendor?.banners || [])

  const liveCampaigns = activeCampaigns.filter((c) => c.campaignBanner && (!c.customerOnly || !!session?.user?.id))

  const campaignSlides: Slide[] = liveCampaigns.map((c) => ({
    src: formatImageURLGoogle(c.campaignBanner!),
    alt: c.name,
    link: navigateUrl(`/c/${c.handle}`, pathname),
  }))

  const bannerSlides: Slide[] = liveVendorBanners.map((b: any, i: number) => ({
    src: formatImageURL(b.image),
    alt: `Banner ${i + 1}`,
    link: b.link && b.link.trim() !== '' ? b.link : undefined,
  }))

  const slides = [...campaignSlides, ...bannerSlides]
  if (slides.length === 0) return null

  const ratio = vendor?.bannerAspectRatio && RATIO_CLASS[vendor.bannerAspectRatio] ? vendor.bannerAspectRatio : '9/4'
  const ratioClass = RATIO_CLASS[ratio]
  const dims = RATIO_DIMS[ratio]
  const fullWidth = !!vendor?.bannerFullWidth

  const wrapperClass = fullWidth ? 'w-full' : 'cont py-4 sm:py-8'
  const itemRoundedClass = fullWidth ? '' : 'rounded-lg sm:rounded-3xl'

  return (
    <div className={wrapperClass}>
      <Carousel className="max-w-none! cursor-grab active:cursor-grabbing" autoPlay delay={5000}>
        {slides.map((slide, index) => {
          const inner = (
            <div className={`relative w-full overflow-hidden select-none ${ratioClass} ${itemRoundedClass}`}>
              <Image
                loader={myLoader}
                src={slide.src}
                fill
                draggable={false}
                sizes={fullWidth ? '100vw' : '(min-width: 1280px) 1280px, 100vw'}
                placeholder="blur"
                blurDataURL={`data:image/svg+xml;base64,${toBase64(shimmer(dims.w, dims.h))}`}
                className="object-cover"
                alt={slide.alt}
                priority={index === 0}
              />
            </div>
          )
          return (
            <CarouselItem key={`Slide-${index}`}>
              {slide.link ? (
                <a href={slide.link} aria-label={slide.alt} draggable={false} className="block">
                  {inner}
                </a>
              ) : (
                inner
              )}
            </CarouselItem>
          )
        })}
      </Carousel>
    </div>
  )
}

const myLoader = ({ src, width, quality }: any) => {
  return `${src}?w=${width}&q=${quality || 75}`
}
